import { useFormik } from "formik";
import * as Yup from "yup";

import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  useCreateFictionalUserMutation,
  useUpdateUserMutation,
  useGetUserByIdQuery,
} from "../../store/services/users";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { EditableUser } from "../../models/users";

interface UserFormProps {
  userData?: EditableUser;
}

const getApiError = (err: any, fallback: string): string =>
  err?.data?.error?.message || err?.data?.message || fallback;

export const UserForm = ({ userData = undefined }: UserFormProps) => {
  const [createUser, { isLoading: isLoadingCreate }] =
    useCreateFictionalUserMutation();
  const [updateUser, { isLoading: isLoadingUpdate }] =
    useUpdateUserMutation();
  const navigate = useNavigate();

  const entityId = userData?.id != null ? String(userData.id) : "";
  const { data: cachedUser, isFetching: isEntityFetching } = useGetUserByIdQuery(
    entityId,
    { skip: !entityId }
  );
  const entity = cachedUser ?? userData;

  const isSubmitBlocked =
    isEntityFetching || isLoadingCreate || isLoadingUpdate;

  const goBack = () => navigate(-1);

  const { handleSubmit, touched, errors, values, handleBlur, handleChange } =
    useFormik({
      initialValues: {
        email: entity?.email || "",
        firstName: entity?.firstName || "",
        lastName: entity?.lastName || "",
      },
      enableReinitialize: !!entity,
      validationSchema: Yup.object({
        email: Yup.string().email("Correo inválido").required("Campo requerido"),
        firstName: Yup.string().required("Campo requerido"),
        lastName: Yup.string().required("Campo requerido"),
      }),
      onSubmit: ({ email, firstName, lastName }) => {
        if (isEntityFetching) return;

        if (userData && userData.id) {
          updateUser({
            id: userData.id,
            email: email,
            firstName: firstName,
            lastName: lastName,
            userPhoneCode: "54",
            userPhone: 123456789,
            birthDate: "1990-01-01",
            country: "ARG",
          } as any)
            .unwrap()
            .then(() => {
              toast.success("Usuario editado exitosamente!");
              goBack();
            })
            .catch((err) =>
              toast.error(getApiError(err, "No se pudo editar el usuario"))
            );
        } else {
          createUser({
            email: email,
            firstName: firstName,
            lastName: lastName,
          })
            .unwrap()
            .then(() => {
              toast.success("Usuario creado exitosamente!");
              goBack();
            })
            .catch((err) =>
              toast.error(getApiError(err, "No se pudo crear el usuario"))
            );
        }
      },
    });

  return (
    <form
      className="flex flex-column justify-content-center align-items-center"
      onSubmit={handleSubmit}
    >
      <div className="col-12 md:col-6 my-2">
        <span className="p-float-label">
          <InputText
            className={`min-w-full ${
              touched.email && errors.email ? "p-invalid" : ""
            }`}
            id="email"
            name="email"
            keyfilter="email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            disabled={isLoadingCreate || isLoadingUpdate}
          />
          <label htmlFor="email">Correo</label>
        </span>
        {touched.email && errors.email && (
          <small className="p-error">{errors.email}</small>
        )}
      </div>
      <div className="col-12 md:col-6 my-2">
        <span className="p-float-label">
          <InputText
            className={`min-w-full ${
              touched.firstName && errors.firstName ? "p-invalid" : ""
            }`}
            id="firstName"
            name="firstName"
            keyfilter={/^[a-zA-ZáéíóúÁÉÍÓÚ\s]+$/}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.firstName}
            disabled={isLoadingCreate || isLoadingUpdate}
          />
          <label htmlFor="firstName">Nombre/s</label>
        </span>
        {touched.firstName && errors.firstName && (
          <small className="p-error">{errors.firstName}</small>
        )}
      </div>
      <div className="col-12 md:col-6 my-2">
        <span className="p-float-label">
          <InputText
            className={`min-w-full ${
              touched.lastName && errors.lastName ? "p-invalid" : ""
            }`}
            id="lastName"
            name="lastName"
            keyfilter={/^[a-zA-ZáéíóúÁÉÍÓÚ\s]+$/}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lastName}
            disabled={isLoadingCreate || isLoadingUpdate}
          />
          <label htmlFor="lastName">Apellido/s</label>
        </span>
        {touched.lastName && errors.lastName && (
          <small className="p-error">{errors.lastName}</small>
        )}
      </div>
      <div className="col-12 flex justify-content-center">
        <Button
          icon="pi pi-check"
          severity="success"
          type="submit"
          label={userData ? "Guardar" : "Crear"}
          disabled={isSubmitBlocked}
          loading={isLoadingCreate || isLoadingUpdate}
        />
      </div>
    </form>
  );
};